import apiClient from './apiClient';

export interface DashboardStats {
  totalPets: number;
  totalUsers: number;
  totalCategories: number;
  totalBreeds: number;
  activePosts: number;
  totalChats: number;
  monthlyRevenue: number;
  dailyViews: number;
  petStats: {
    total: number;
    available: number;
    sold: number;
    featured: number;
    premium: number;
    pendingApproval: number;
  };
  growthStats: {
    pets: { value: number; trend: 'up' | 'down' };
    users: { value: number; trend: 'up' | 'down' };
    posts: { value: number; trend: 'up' | 'down' };
    revenue: { value: number; trend: 'up' | 'down' };
  };
  recentPets: Array<{
    id: string;
    title: string;
    owner: string;
    price: number;
    image: string;
    status: 'active' | 'pending' | 'inactive';
    views: number;
    createdAt: string;
  }>;
  topCategories: Array<{
    name: string;
    count: number;
    percentage: number;
  }>;
  recentActivities: Array<{
    user: string;
    action: string;
    time: string;
    type: 'pet' | 'user' | 'post' | 'follow';
  }>;
}

class DashboardService {
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      // Use real backend analytics instead of fabricated numbers.
      const [analyticsRes, catAnalyticsRes, petsRes, usersRes, categoriesRes, breedsRes, petStatsRes] = await Promise.all([
        apiClient.get('/analytics/dashboard?period=30d'),
        apiClient.get('/analytics/categories'),
        apiClient.get('/pets?limit=5&sortBy=createdAt&sortOrder=desc'),
        apiClient.get('/users?limit=5&sortBy=createdAt&sortOrder=desc'),
        apiClient.get('/pet-categories'),
        apiClient.get('/breeds'),
        apiClient.get('/pets/admin/stats').catch(() => ({ data: { data: null } }))
      ]);

      const summary = analyticsRes.data.data?.summary || {};
      const growth = analyticsRes.data.data?.growth || {};
      const petCategoriesAnalytics: any[] = catAnalyticsRes.data.data?.petCategories || [];

      const totalPets = summary.totalPets ?? (petsRes.data.data?.pagination?.totalItems || 0);
      const totalUsers = summary.totalUsers ?? (usersRes.data.data?.pagination?.totalItems || usersRes.data.data?.users?.length || 0);
      const totalPosts = summary.totalPosts ?? 0;
      const totalCategories = categoriesRes.data.data?.pagination?.totalItems || categoriesRes.data.data?.categories?.length || 0;
      const totalBreeds = breedsRes.data.data?.pagination?.totalItems || breedsRes.data.data?.breeds?.length || 0;
      const totalViews = petCategoriesAnalytics.reduce((sum, c) => sum + (c.totalViews || 0), 0);

      const ps = petStatsRes.data.data || {};
      const petStats = {
        total: ps.total ?? totalPets,
        available: ps.available ?? 0,
        sold: ps.sold ?? 0,
        featured: ps.featured ?? 0,
        premium: ps.premium ?? 0,
        pendingApproval: ps.pendingApproval ?? 0,
      };

      const recentPets = (petsRes.data.data?.pets || []).slice(0, 5).map((pet: any) => ({
        id: pet._id,
        title: pet.title,
        owner: pet.owner?.name || 'Unknown',
        price: pet.price,
        image: pet.images?.[0]?.url || '',
        status: pet.isActive ? 'active' : 'inactive',
        views: pet.viewCount || 0,
        createdAt: pet.createdAt
      }));

      // Real category distribution: share of total listings per category.
      const topCategories = [...petCategoriesAnalytics]
        .sort((a, b) => (b.petCount || 0) - (a.petCount || 0))
        .slice(0, 5)
        .map((c: any) => ({
          name: c.category?.name || 'Unknown',
          count: c.petCount || 0,
          percentage: totalPets > 0 ? Math.round(((c.petCount || 0) / totalPets) * 100) : 0
        }));

      const toTrend = (g: any) => ({
        value: Math.abs(Number(g?.growth ?? 0)),
        trend: (g?.trend === 'down' ? 'down' : 'up') as 'up' | 'down'
      });

      // Real recent activity derived from the latest pets and users.
      const recentUsers = usersRes.data.data?.users || [];
      const recentActivities = [
        ...recentPets.slice(0, 2).map((p: any) => ({
          user: p.owner, action: `listed "${p.title}"`, time: p.createdAt, type: 'pet' as const
        })),
        ...recentUsers.slice(0, 2).map((u: any) => ({
          user: u.name || u.username || 'New user', action: 'joined Jivbook', time: u.createdOn || u.createdAt, type: 'user' as const
        }))
      ];

      const stats: DashboardStats = {
        totalPets,
        totalUsers,
        totalCategories,
        totalBreeds,
        activePosts: totalPosts,
        totalChats: 0, // no aggregate chat metric exposed by the API yet
        monthlyRevenue: 0, // marketplace has no payment/transaction system yet
        dailyViews: totalViews,
        petStats,
        growthStats: {
          pets: toTrend(growth.pets),
          users: toTrend(growth.users),
          posts: toTrend(growth.posts),
          revenue: { value: 0, trend: 'up' }
        },
        recentPets,
        topCategories,
        recentActivities
      };

      return stats;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw new Error('Failed to fetch dashboard statistics');
    }
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;

